import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { TableContext } from 'Context/Table'
import { UIContext } from 'Context/UI'

import ProductCell from 'Presentation/Components/RequestCard/Components/ProductCell'
import MultiplePriceCell from 'Presentation/Components/RequestCard/Components/MultiplePrice'

import { useInquiryStore } from 'Store/Inquiry'
import { useLang } from '_metronic/i18n/Metronici18n'
import { shallow } from 'zustand/shallow'
import formatNumberCommas from 'Utils/formatNumberCommas'
import Swal from 'sweetalert2'
import moment from 'moment'

const ViewModel = () => {
	const navigate = useNavigate()
	const currentLanguage = useLang()
	const { id } = useParams()
	const { setShowContentToolbar, setCustomContentToolbar } = useContext(UIContext)
	const { updateLoading, updateError } = useContext(TableContext)
	const { data, getOne, accept, deny, clearState } = useInquiryStore(
		state => ({
			data: state.selected,
			getOne: state.getOne,
			accept: state.accept,
			deny: state.deny,
			clearState: state.clearState,
		}),
		shallow
	)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [isLoading, setIsLoading] = useState(false)
	const [expandedQuantity, setExpandedQuantity] = useState<boolean[]>([])
	const [properties, setProperties] = useState<{
		productLineParent: string
		productLine: string
		options: any[]
		dimension: any
		thickness: any
		outerMaterial: any
		innerMaterial: any
		printingOption: any
		sustainabilities: any[]
		certificates: any[]
	}>({
		productLineParent: '',
		productLine: '',
		options: [],
		dimension: {},
		thickness: {},
		outerMaterial: {},
		innerMaterial: {},
		printingOption: {},
		sustainabilities: [],
		certificates: [],
	})

	const isRejected = data?.status === '30'
	const canMakeDecision =
		data?.status === '10' && moment(data?.oemRequest?.expiredAt).isAfter(moment())

	const expiredAt = useMemo(() => {
		if (data?.oemRequest?.expiredAt) {
			const isExpired = moment().isAfter(data?.oemRequest?.expiredAt)
			if (isExpired) {
				return 'หมดอายุ'
			} else {
				return moment(data?.oemRequest?.expiredAt).diff(moment(), 'hours')
			}
		} else {
			return null
		}
	}, [data])

	const tableData = useMemo(
		() => data?.oemRequest?.items ?? [],
		// (data?.oemRequest?.items ?? []).reduce((acc: any[], item: any) => {
		// 	;(item.quantities ?? []).forEach((quantity: any, idx: number) => {
		// 		const i = {
		// 			id: item.id,
		// 			product: item.product,
		// 			options: item.options,
		// 			accessories: item.accessories,
		// 			thickness: item.thickness,
		// 			outerMaterial: item.outerMaterial,
		// 			innerMaterial: item.innerMaterial,
		// 			printingOption: item.printingOption,
		// 			forming: item.forming,
		// 			dimension: item.dimension,
		// 			sustainabilities: item.sustainabilities,
		// 			certificates: data?.oemRequest?.certificates,
		// 			quantities: {
		// 				quantity: quantity.quantity,
		// 				targetPrice: quantity.targetPrice,
		// 				unit: currentLanguage === 'en' ? 'pcs' : 'ชิ้น',
		// 			},
		// 		}

		// 		acc.push(i)
		// 	})

		// 	return acc
		// }, []),
		[data]
	)

	const fetchData = () => {
		if (id) {
			setIsLoading(true)
			updateLoading(true)

			getOne(id).then(({ success, data }) => {
				if (!success) {
					Swal.fire({
						title: 'เกิดข้อผิดพลาด',
						text: data,
						icon: 'error',
						confirmButtonText: 'ปิด',
						customClass: {
							confirmButton: 'btn btn-sm btn-kumopack-primary-600 text-kumopack-base-white',
						},
					})
					setIsLoading(false)
					updateLoading(false)
					updateError(true)
				} else {
					setExpandedQuantity(data?.oemRequest?.quantities?.map(() => false) ?? [])

					// Set Properties
					const oemRequest = data?.oemRequest
					setProperties({
						productLineParent: oemRequest.product?.productLine?.parent?.nameTh,
						productLine: oemRequest.product?.productLine?.nameTh,
						options: oemRequest.options,
						dimension: oemRequest.dimension,
						thickness: oemRequest.thickness,
						outerMaterial: oemRequest.outerMaterial,
						innerMaterial: oemRequest.innerMaterial,
						printingOption: oemRequest.printingOption,
						sustainabilities: oemRequest.sustainabilities,
						certificates: oemRequest.certificates,
					})

					setIsLoading(false)
					updateLoading(false)
					updateError(false)
				}
			})
		}
	}

	const back = () => {
		navigate('/oem/orders')
	}

	const reject = () => {
		Swal.fire({
			icon: 'error',
			text: 'ยืนยันการปฎิเสธข้อเสนอนี้',
			confirmButtonText: 'ยืนยัน',
			cancelButtonText: 'ยกเลิก',
			showCancelButton: true,
			customClass: {
				confirmButton: 'btn btn-sm btn-kumopack-primary-600 text-kumopack-base-white',
				cancelButton: 'btn btn-sm white-button text-kumopack-grey-700 fw-bold',
			},
		}).then(result => {
			if (result.isConfirmed) {
				rejectConfirm()
			}
		})
	}

	const rejectConfirm = () => {
		setIsSubmitting(true)

		deny(id ?? '').then(({ success, data }) => {
			if (success) {
				Swal.fire({
					title: 'ปฏิเสธข้อเสนอเสร็จสิ้น',
					text: 'ปฏิเสธข้อเสนอเสร็จสิ้น',
					icon: 'success',
					confirmButtonText: 'ปิด',
					showCancelButton: false,
					customClass: {
						confirmButton: 'btn btn-sm btn-kumopack-primary-600 text-kumopack-base-white',
						cancelButton: 'btn btn-sm white-button text-kumopack-grey-700 fw-bold',
					},
				}).then(() => {
					fetchData()
					setIsSubmitting(false)
				})
			} else {
				Swal.fire({
					title: 'ปฏิเสธข้อเสนอไม่สำเร็จ',
					text: data,
					icon: 'error',
					confirmButtonText: 'ปิด',
					showCancelButton: false,
					customClass: {
						confirmButton: 'btn btn-sm btn-kumopack-primary-600 text-kumopack-base-white',
						cancelButton: 'btn btn-sm white-button text-kumopack-grey-700 fw-bold',
					},
				}).then(() => {
					setIsSubmitting(false)
				})
			}
		})
	}

	const confirm = () => {
		Swal.fire({
			icon: 'success',
			text: 'ยืนยันการตอบรับข้อเสนอนี้',
			confirmButtonText: 'ยืนยัน',
			cancelButtonText: 'ยกเลิก',
			showCancelButton: true,
			customClass: {
				confirmButton: 'btn btn-sm btn-kumopack-primary-600 text-kumopack-base-white',
				cancelButton: 'btn btn-sm white-button text-kumopack-grey-700 fw-bold',
			},
		}).then(result => {
			if (result.isConfirmed) {
				acceptConfirm()
			}
		})
	}

	const acceptConfirm = () => {
		setIsSubmitting(true)

		accept(id ?? '').then(({ success, data }) => {
			if (success) {
				Swal.fire({
					title: 'ตอบรับข้อเสนอจากผู้ผลิตสำเร็จ',
					text: 'ตอบรับข้อเสนอจากผู้ผลิตสำเร็จ',
					icon: 'success',
					confirmButtonText: 'ปิด',
					showCancelButton: false,
					customClass: {
						confirmButton: 'btn btn-sm btn-kumopack-primary-600 text-kumopack-base-white',
						cancelButton: 'btn btn-sm white-button text-kumopack-grey-700 fw-bold',
					},
				}).then(() => {
					navigate(`/oem/quotations/${data?.quotationUUID}`)
				})
			} else {
				Swal.fire({
					title: 'ตอบรับข้อเสนอจากผู้ผลิตไม่สำเร็จ',
					text: data,
					icon: 'error',
					confirmButtonText: 'ปิด',
					showCancelButton: false,
					customClass: {
						confirmButton: 'btn btn-sm btn-kumopack-primary-600 text-kumopack-base-white',
						cancelButton: 'btn btn-sm white-button text-kumopack-grey-700 fw-bold',
					},
				}).then(() => {
					setIsSubmitting(false)
				})
			}
		})
	}

	const toggleEdit = (idx: number) => {
		setExpandedQuantity(prev => {
			const copy = [...prev]
			copy[idx] = !copy[idx]
			return copy
		})
	}

	const TABLE_COLUMNS: any[] = [
		{
			Header: 'สินค้าที่สั่งผลิต',
			accessor: 'product',
			width: '70%',
			minWidth: '70%',
			Cell: ({ value, row }: any) => (
				<ProductCell
					{...value}
					dimension={row.original?.dimension}
					options={row.original?.options}
					accessories={row.original?.accessories}
					thickness={row.original?.thickness}
					outerMaterial={row.original?.outerMaterial}
					innerMaterial={row.original?.innerMaterial}
					printingOption={row.original?.printingOption}
					forming={row.original?.forming}
					sustainabilities={row.original?.sustainabilities}
					certificates={row.original?.certificates}
				/>
			),
		},
		{
			Header: () => <div className="text-end">จำนวนที่สั่งผลิต</div>,
			headerRight: true,
			accessor: 'quantities',
			width: '20%',
			minWidth: '20%',
			Cell: ({ value }: any) => (
				<React.Fragment>
					{value.map((item: any, index: number) => (
						<div key={index} className="text-end">
							<div className={'d-inline-block text-kumopack-grey-900 fw-bold fs-4'}>
								x {formatNumberCommas(item.quantity)}
							</div>
							<div
								className="ms-2 d-inline-block text-kumopack-grey-600 fs-6"
								style={{ whiteSpace: 'nowrap' }}>
								{currentLanguage === 'en' ? 'pcs' : 'ชิ้น'}
							</div>
						</div>
					))}
				</React.Fragment>
			),
		},
		{
			Header: () => <div className="text-end">ราคาที่เสนอ</div>,
			headerRight: true,
			accessor: 'targetPrice',
			width: '20%',
			minWidth: '20%',
			Cell: ({ row }: any) => {
				return (
					<MultiplePriceCell
						prices={(row.original.quantities ?? []).map((item: any) => ({
							price: item?.targetPrice ?? 0,
						}))}
					/>
				)
			},
		},
	]

	useEffect(() => {
		fetchData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		setShowContentToolbar(false)
		setCustomContentToolbar(null)

		return () => {
			setShowContentToolbar(true)
			setCustomContentToolbar(null)
			clearState()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return {
		isSubmitting,
		isLoading,
		isRejected,
		canMakeDecision,
		data: {
			...data,
			product: data?.oemRequest?.product,
			quantities: data?.oemRequest?.quantities ?? [],
		},
		tableData,
		expiredAt,
		properties,
		TABLE_COLUMNS,
		expandedQuantity,
		confirm,
		reject,
		back,
		toggleEdit,
	}
}

export default ViewModel
